// Version management for handling app updates and cache invalidation
import storageManager from './storageManager';

const APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0';
const BUILD_TIME = import.meta.env.VITE_BUILD_TIME || new Date().toISOString();
const VERSION_KEY = 'app_version';
const LAST_CHECK_KEY = 'version_check';
const CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

interface VersionInfo {
  version: string;
  buildTime: string;
  timestamp: number;
  buildHash?: string;
}

class VersionManager {
  private currentVersion: string;
  private lastCheck: number;

  constructor() {
    this.currentVersion = APP_VERSION;
    this.lastCheck = parseInt(
      storageManager.getItem<string>(LAST_CHECK_KEY) || '0'
    );
    
    // Initialize storage management on first run
    storageManager.initialize();
  }

  /**
   * Check if the app version has changed since last visit
   */
  public checkVersion(): boolean {
    const storedVersion = storageManager.getItem<VersionInfo>(VERSION_KEY);
    
    if (!storedVersion) {
      // First time visit, store current version
      this.updateStoredVersion();
      return false;
    }

    try {
      const versionInfo: VersionInfo = storedVersion;
      
      if (versionInfo.version !== this.currentVersion || versionInfo.buildTime !== BUILD_TIME) {
        console.log(`Version mismatch detected: ${versionInfo.version}@${versionInfo.buildTime} -> ${this.currentVersion}@${BUILD_TIME}`);
        return true;
      }
    } catch (error) {
      console.error('Error parsing stored version:', error);
      return true;
    }

    return false;
  }

  /**
   * Update the stored version to current
   */
  public updateStoredVersion(): void {
    const versionInfo: VersionInfo = {
      version: this.currentVersion,
      buildTime: BUILD_TIME,
      timestamp: Date.now(),
      buildHash: this.getBuildHash()
    };
    
    storageManager.setItem<VersionInfo>(VERSION_KEY, versionInfo);
    storageManager.setItem<string>(LAST_CHECK_KEY, Date.now().toString());
  }

  /**
   * Clear all app data when version mismatch is detected
   */
  public async clearAppData(): Promise<void> {
    console.log('Clearing app data due to version mismatch...');
    
    try {
      // Clear version-specific storage while preserving user preferences
      storageManager.clearVersionSpecific();

      // Clear sessionStorage
      sessionStorage.clear();

      // Clear IndexedDB if it exists
      if ('indexedDB' in window) {
        try {
          const databases = await indexedDB.databases();
          for (const db of databases) {
            if (db.name?.startsWith('zippyhealth') || db.name?.includes('supabase')) {
              indexedDB.deleteDatabase(db.name);
            }
          }
        } catch (error) {
          console.warn('Could not clear IndexedDB:', error);
        }
      }

      // Clear service worker caches if they exist
      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys();
          for (const cacheName of cacheNames) {
            await caches.delete(cacheName);
          }
        } catch (error) {
          console.warn('Could not clear caches:', error);
        }
      }

    } catch (error) {
      console.error('Error clearing app data:', error);
    }
  }

  /**
   * Force reload the application
   */
  public forceReload(): void {
    // Add timestamp to prevent cache
    const timestamp = Date.now();
    window.location.href = `${window.location.origin}${window.location.pathname}?v=${timestamp}`;
  }

  /**
   * Check if enough time has passed since last check
   */
  public shouldCheckForUpdates(): boolean {
    const now = Date.now();
    return (now - this.lastCheck) > CHECK_INTERVAL;
  }

  /**
   * Periodically check for updates
   */
  public startPeriodicCheck(): void {
    setInterval(() => {
      if (this.shouldCheckForUpdates()) {
        this.checkForUpdates();
      }
    }, CHECK_INTERVAL);
  }

  /**
   * Check for updates by making a lightweight request
   */
  private async checkForUpdates(): Promise<void> {
    try {
      // Make a request to the root to see if we get a different build
      const response = await fetch(`${window.location.origin}/?t=${Date.now()}`, {
        method: 'HEAD',
        cache: 'no-cache'
      });
      
      if (response.ok) {
        const etag = response.headers.get('etag');
        const lastModified = response.headers.get('last-modified');
        
        if (etag || lastModified) {
          const storedVersion = storageManager.getItem<VersionInfo>(VERSION_KEY);
          if (storedVersion) {
            const versionInfo: VersionInfo = storedVersion;
            const storedHash = versionInfo.buildHash;
            
            if (storedHash && storedHash !== (etag || lastModified)) {
              console.log('Update detected via headers');
              this.handleVersionMismatch();
            }
          }
        }
      }
      
      storageManager.setItem<string>(LAST_CHECK_KEY, Date.now().toString());
    } catch (error) {
      console.warn('Could not check for updates:', error);
    }
  }

  /**
   * Handle version mismatch by clearing data and reloading
   */
  public async handleVersionMismatch(): Promise<void> {
    console.log('Handling version mismatch...');
    
    // Show user notification
    this.showUpdateNotification();
    
    // Clear data
    await this.clearAppData();
    
    // Update version
    this.updateStoredVersion();
    
    // Reload after a short delay
    setTimeout(() => {
      this.forceReload();
    }, 1000);
  }

  /**
   * Show a user-friendly notification about the update
   */
  private showUpdateNotification(): void {
    // Create a temporary notification element
    const notification = document.createElement('div');
    notification.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #dc2626;
        color: white;
        padding: 16px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        max-width: 300px;
      ">
        <div style="display: flex; align-items: center; gap: 8px;">
          <div style="flex-shrink: 0;">🔄</div>
          <div>
            <div style="font-weight: 600; margin-bottom: 4px;">ZippyHealth Updated!</div>
            <div style="opacity: 0.9;">Refreshing to load the latest version...</div>
          </div>
        </div>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Remove notification after reload
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 5000);
  }

  /**
   * Get a hash to identify the current build
   */
  private getBuildHash(): string {
    // Use a combination of factors to identify the build
    const scripts = Array.from(document.querySelectorAll('script[src]'));
    const links = Array.from(document.querySelectorAll('link[href]'));
    
    const hashes = [
      ...scripts.map(script => (script as HTMLScriptElement).src),
      ...links.map(link => (link as HTMLLinkElement).href)
    ].filter(src => src.includes('/assets/') || src.includes('index'));
    
    return hashes.join('|').substring(0, 50);
  }

  /**
   * Get current version info
   */
  public getVersionInfo(): VersionInfo {
    return {
      version: this.currentVersion,
      buildTime: BUILD_TIME,
      timestamp: Date.now(),
      buildHash: this.getBuildHash()
    };
  }
}

export const versionManager = new VersionManager();
export default versionManager; 
